import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, ChevronLeft, ChevronRight, Clock3 } from "lucide-react";
import { API, Shell, useT } from "./Shell";
import "./testTheme.css";

export default function TestEngine() {
  const navigate = useNavigate();
  const { topicId } = useParams();
  const T = useT();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [left, setLeft] = useState(null);
  const [qLeft, setQLeft] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const offsetRef = useRef(0);
  const submittedRef = useRef(false);
  const dataRef = useRef(null);
  dataRef.current = data;

  useEffect(() => {
    const studentId = localStorage.getItem("pjp-student");
    if (!studentId) { navigate("/physics-test"); return; }
    if (!topicId) { navigate("/physics-test/topics"); return; }
    axios.post(`${API}/test/start`, { student_id: studentId, topic_id: topicId })
      .then(({ data: d }) => {
        if (d.status === "submitted") { navigate(`/physics-test/result/${d.attempt_id}`); return; }
        offsetRef.current = d.server_now * 1000 - Date.now();
        setData(d);
        setAnswers(d.answers || {});
      })
      .catch((err) => setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not start the test."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, topicId]);

  const doSubmit = async () => {
    const d = dataRef.current;
    if (!d) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/test/submit`, { attempt_id: d.attempt_id });
      navigate(`/physics-test/result/${d.attempt_id}`);
    } catch {
      submittedRef.current = false;
      setSubmitting(false);
      setError(T.e_submit_fail);
    }
  };

  useEffect(() => {
    if (!data) return undefined;
    const tick = () => {
      const remaining = Math.max(0, Math.round((data.deadline * 1000 - (Date.now() + offsetRef.current)) / 1000));
      setLeft(remaining);
      if (remaining <= 0 && !submittedRef.current) { submittedRef.current = true; doSubmit(); }
    };
    tick();
    const timer = setInterval(tick, 1000);
    const onVis = () => { if (document.hidden && !submittedRef.current) axios.post(`${API}/test/tab`, { attempt_id: data.attempt_id }).catch(() => {}); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Per-question countdown (when the admin enables it): resets on every question change,
  // auto-advances at 0, auto-submits after the last question. Overall deadline still rules.
  useEffect(() => {
    if (!data || !data.per_question_seconds) { setQLeft(null); return undefined; }
    const qDeadline = Date.now() + offsetRef.current + data.per_question_seconds * 1000;
    setQLeft(data.per_question_seconds);
    const t = setInterval(() => {
      const rem = Math.max(0, Math.round((qDeadline - (Date.now() + offsetRef.current)) / 1000));
      setQLeft(rem);
      if (rem <= 0) {
        if (idx < data.questions.length - 1) setIdx((i) => i + 1);
        else if (!submittedRef.current) { submittedRef.current = true; doSubmit(); }
      }
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, idx]);

  const select = (qid, opt) => {
    setAnswers((current) => ({ ...current, [qid]: opt }));
    axios.post(`${API}/test/answer`, { attempt_id: data.attempt_id, question_id: qid, selected: opt }).catch(() => {});
  };

  if (error) return <Shell><div className="tp-wrap"><div className="tp-hero"><h1 style={{ fontSize: 30 }}>{T.e_hold}</h1><p className="tp-error" data-testid="tp-engine-error">{error}</p><a className="tp-btn secondary" href="/physics-test/topics">{T.e_back}</a></div></div></Shell>;
  if (!data) return <Shell><div className="tp-wrap"><div className="tp-hero"><p className="tp-muted" data-testid="tp-preparing">{T.e_prep}</p><p className="tp-note" style={{ maxWidth: 420, margin: "10px auto 0" }} data-testid="tp-preparing-ai">{T.e_prep_ai}</p></div></div></Shell>;

  const question = data.questions[idx];
  const answeredCount = Object.keys(answers).length;
  const mm = String(Math.floor((left ?? 0) / 60)).padStart(2, "0");
  const ss = String((left ?? 0) % 60).padStart(2, "0");

  return (
    <Shell>
      <div className="tp-wrap" style={{ maxWidth: 820, paddingBottom: 60 }}>
        <div className="tp-honesty" data-testid="tp-honesty"><AlertTriangle size={15} /> {T.e_honesty}</div>
        {data.topic?.name && <div style={{ textAlign: "center", marginTop: 10 }}><span className="tp-pill info" data-testid="tp-engine-topic">{data.topic.name.toUpperCase()}</span></div>}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap", margin: "18px 0 14px" }}>
          <span className={left !== null && left <= 60 ? "tp-timer low" : "tp-timer"} data-testid="tp-timer"><Clock3 size={17} /> {mm}:{ss}</span>
          {qLeft !== null && (
            <span className={qLeft <= 10 ? "tp-timer low" : "tp-timer"} style={{ fontSize: 13, padding: "6px 12px" }} data-testid="tp-qtimer">
              {T.e_qt} {qLeft}s
            </span>
          )}
          <span className="tp-pill info" data-testid="tp-qcount">{T.e_qof} {idx + 1} {T.e_of} {data.questions.length}</span>
        </div>
        <div className="tp-progress" data-testid="tp-progress"><i style={{ width: `${(answeredCount / data.questions.length) * 100}%` }} /></div>
        <p className="tp-note" style={{ margin: "8px 0 18px" }}>{T.e_answered_line(answeredCount, data.questions.length)}</p>
        <div className="tp-qcard" data-testid="tp-question-card">
          <div className="tp-qmeta">{question.topic && <span className="tp-pill info">{question.topic.toUpperCase()}</span>}{question.difficulty && <span className="tp-pill warn">{question.difficulty.toUpperCase()}</span>}</div>
          <h3 data-testid="tp-question-text">{question.text}</h3>
          {question.image_url && <img src={question.image_url} alt="Question diagram" />}
          {["A", "B", "C", "D"].map((key) => (
            <button type="button" key={key} className={answers[question.question_id] === key ? "tp-option selected" : "tp-option"} onClick={() => select(question.question_id, key)} data-testid={`tp-option-${key}`}>
              <span className="key">{key}</span> {question.options[key]}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20, gap: 12, flexWrap: "wrap" }}>
          <button className="tp-btn ghost small" onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0} data-testid="tp-prev"><ChevronLeft size={16} /> {T.e_prev}</button>
          <div className="tp-qnav">{data.questions.map((q, i) => <button type="button" key={q.question_id} className={`${answers[q.question_id] ? "answered" : ""} ${i === idx ? "current" : ""}`} onClick={() => setIdx(i)} data-testid={`tp-nav-${i}`}>{i + 1}</button>)}</div>
          {idx < data.questions.length - 1
            ? <button className="tp-btn secondary small" onClick={() => setIdx(idx + 1)} data-testid="tp-next">{T.e_next} <ChevronRight size={16} /></button>
            : <button className="tp-btn small" onClick={() => setConfirming(true)} data-testid="tp-submit-open">{T.e_submit}</button>}
        </div>
        <div style={{ textAlign: "center", marginTop: 22 }}><button className="tp-btn small" onClick={() => setConfirming(true)} data-testid="tp-submit-bottom">{T.e_submit_count(answeredCount, data.questions.length)}</button></div>
        {confirming && <div className="tp-modal-backdrop" data-testid="tp-confirm-modal"><div className="tp-modal">
          <h3>{T.e_confirm_title}</h3>
          <p className="tp-muted">{T.e_confirm_answered(answeredCount, data.questions.length)} {T.e_confirm_body}</p>
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button className="tp-btn" style={{ flex: 1 }} onClick={() => { submittedRef.current = true; setConfirming(false); doSubmit(); }} disabled={submitting} data-testid="tp-submit-confirm">{submitting ? T.e_submitting : T.e_yes}</button>
            <button className="tp-btn ghost" style={{ flex: 1 }} onClick={() => setConfirming(false)} data-testid="tp-submit-cancel">{T.e_keep}</button>
          </div>
        </div></div>}
      </div>
    </Shell>
  );
}
